import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { DashboardSkeleton } from '../components/shared/LoaderSkeleton';
import Text from '../components/shared/Text';

// Dashboard Page - Premium UI/UX overhaul
function Dashboard() {
    const [stats, setStats] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        loadDashboard();
    }, []);

    const loadDashboard = async () => {
        try {
            setLoading(true);
            const response = await api.getDashboard();
            if (response.success) {
                setStats(response.data);
            }
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return <DashboardSkeleton />;
    }

    if (error) {
        return (
            <div className="mt-8 max-w-2xl mx-auto">
                <div className="bg-rose-50 border border-rose-200 text-rose-700 px-6 py-4 rounded-2xl flex items-center gap-4 shadow-sm">
                    <svg className="w-6 h-6 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <Text weight="medium">Error loading details: {error}</Text>
                </div>
            </div>
        );
    }

    const userName = (window as any).CURRENT_USER?.name || 'Administrator';

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-10 animate-in fade-in duration-700">
            {/* Header with Greeting */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div>
                    <Text variant="h1">
                        Welcome back, <Text as="span" color="indigo">{userName}</Text>
                    </Text>
                    <Text variant="desc" className="mt-2">Here's what's happening with your business today.</Text>
                </div>
            </div>

            {/* Quick Actions - Mobile Friendly */}
            <div className="bg-white rounded-lg border border-slate-100 shadow-xl shadow-slate-200/50 p-6 sm:p-8">
                <Text variant="label" color="slate" className="mb-6">Quick Actions</Text>
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-4">
                    <Link
                        to="/invoices/new"
                        className="flex flex-col items-center justify-center gap-3 p-4 rounded-2xl bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-all group border border-indigo-100/50"
                    >
                        <div className="w-12 h-12 rounded-xl bg-white shadow-sm flex items-center justify-center group-hover:scale-110 transition-transform">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                            </svg>
                        </div>
                        <Text variant="small" weight="bold">New Invoice</Text>
                    </Link>

                    <Link
                        to="/products/manage"
                        className="flex flex-col items-center justify-center gap-3 p-4 rounded-2xl bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-all group border border-emerald-100/50"
                    >
                        <div className="w-12 h-12 rounded-xl bg-white shadow-sm flex items-center justify-center group-hover:scale-110 transition-transform">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                            </svg>
                        </div>
                        <Text variant="small" weight="bold">Manage Products</Text>
                    </Link>

                    <Link
                        to="/customers/new"
                        className="flex flex-col items-center justify-center gap-3 p-4 rounded-2xl bg-amber-50 text-amber-700 hover:bg-amber-100 transition-all group border border-amber-100/50"
                    >
                        <div className="w-12 h-12 rounded-xl bg-white shadow-sm flex items-center justify-center group-hover:scale-110 transition-transform">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                            </svg>
                        </div>
                        <Text variant="small" weight="bold">Add Customer</Text>
                    </Link>

                    <Link
                        to="/restocks/new"
                        className="flex flex-col items-center justify-center gap-3 p-4 rounded-2xl bg-purple-50 text-purple-700 hover:bg-purple-100 transition-all group border border-purple-100/50"
                    >
                        <div className="w-12 h-12 rounded-xl bg-white shadow-sm flex items-center justify-center group-hover:scale-110 transition-transform">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                            </svg>
                        </div>
                        <Text variant="small" weight="bold">Inventory IN</Text>
                    </Link>

                    <Link
                        to="/import"
                        className="hidden lg:flex flex-col items-center justify-center gap-3 p-4 rounded-2xl bg-slate-50 text-slate-700 hover:bg-slate-100 transition-all group border border-slate-100/50"
                    >
                        <div className="w-12 h-12 rounded-xl bg-white shadow-sm flex items-center justify-center group-hover:scale-110 transition-transform">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                            </svg>
                        </div>
                        <Text variant="small" weight="bold">Bulk Import</Text>
                    </Link>
                </div>
            </div>

            {/* Stats Overview */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-gradient-to-br from-indigo-600 to-indigo-700 rounded-lg p-8 text-white relative overflow-hidden group shadow-2xl shadow-indigo-200">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-700"></div>
                    <Text variant="label" className="opacity-80 mb-2">Products</Text>
                    <Text variant="stat" className="mb-2">{stats?.products_count || 0}</Text>
                    <div className="flex items-center gap-2 text-indigo-100/80 text-sm">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                        </svg>
                        Active items in inventory
                    </div>
                </div>

                <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-lg p-8 text-white relative overflow-hidden group shadow-2xl shadow-emerald-200">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-700"></div>
                    <Text variant="label" className="opacity-80 mb-2">Customers</Text>
                    <Text variant="stat" className="mb-2">{stats?.customers_count || 0}</Text>
                    <div className="flex items-center gap-2 text-emerald-100/80 text-sm">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                        </svg>
                        Growing relationships
                    </div>
                </div>

                <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg p-8 text-white relative overflow-hidden group shadow-2xl shadow-amber-200">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-700"></div>
                    <Text variant="label" className="opacity-80 mb-2">Receivables</Text>
                    <Text variant="stat" className="mb-2">₹{(stats?.total_receivables || 0).toLocaleString()}</Text>
                    <div className="flex items-center gap-2 text-amber-100/80 text-sm">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        To be collected
                    </div>
                </div>

                <div className="bg-gradient-to-br from-rose-500 to-rose-600 rounded-lg p-8 text-white relative overflow-hidden group shadow-2xl shadow-rose-200">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-700"></div>
                    <Text variant="label" className="opacity-80 mb-2">Payables</Text>
                    <Text variant="stat" className="mb-2">₹{(stats?.total_payables || 0).toLocaleString()}</Text>
                    <div className="flex items-center gap-2 text-rose-100/80 text-sm">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Upcoming commitments
                    </div>
                </div>
            </div>

            {/* Detailed Insights */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Pending & Low Stock Alerts */}
                <div className="lg:col-span-1 space-y-8">
                    {/* Pending Orders Count */}
                    <div className="bg-white rounded-lg border border-slate-100 shadow-xl shadow-slate-200/50 p-8 flex items-center justify-between">
                        <div>
                            <Text variant="label" color="slate" className="mb-1">Pending Orders</Text>
                            <Text variant="stat" color="primary">{stats?.pending_orders || 0}</Text>
                        </div>
                        <div className="w-14 h-14 bg-rose-50 rounded-2xl flex items-center justify-center text-rose-600">
                            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                            </svg>
                        </div>
                    </div>

                    {/* Pending Installations Count */}
                    <div className="bg-white rounded-lg border border-slate-100 shadow-xl shadow-slate-200/50 p-8 flex items-center justify-between">
                        <div>
                            <Text variant="label" color="slate" className="mb-1">Open Installations</Text>
                            <Text variant="stat" color="primary">{stats?.pending_installations_count || 0}</Text>
                        </div>
                        <div className="w-14 h-14 bg-emerald-50 rounded-2xl flex items-center justify-center text-emerald-600">
                            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                        </div>
                    </div>

                    {/* Low Stock Alerts */}
                    <div className="bg-white rounded-lg border border-slate-100 shadow-xl shadow-slate-200/50 p-8">
                        <div className="flex items-center justify-between mb-6">
                            <Text variant="h3">Stock Alerts</Text>
                            <Link to="/products" className="text-indigo-600 text-xs font-bold hover:underline">Manage All</Link>
                        </div>
                        <div className="space-y-4">
                            {stats?.low_stock_products?.length > 0 ? (
                                stats.low_stock_products.slice(0, 5).map((product: any) => (
                                    <div key={product.id} className="group p-4 bg-slate-50 rounded-2xl border border-slate-100 hover:border-amber-200 transition-all">
                                        <div className="flex items-center justify-between gap-3">
                                            <div className="min-w-0">
                                                <Text weight="bold" truncate>{product.name}</Text>
                                                <Text variant="small" color="slate" tracking="tight">Only {product.stock_quantity} remaining</Text>
                                            </div>
                                            <div className="shrink-0 px-2 py-1 bg-amber-100 text-amber-700 rounded-lg">
                                                <Text variant="label">Low</Text>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="text-center py-8">
                                    <div className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                    <p className="text-sm font-bold text-slate-700">All stock stable</p>
                                    <p className="text-xs text-slate-500 mt-1">No alerts at this moment</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Recent Invoices & Orders */}
                <div className="lg:col-span-2 space-y-8">
                    {/* Recent Invoices Table-like View */}
                    <div className="bg-white rounded-lg border border-slate-100 shadow-xl shadow-slate-200/50 overflow-hidden">
                        <div className="p-8 pb-4 flex items-center justify-between">
                            <Text variant="h3">Recent Invoices</Text>
                            <Link to="/invoices" className="text-indigo-600 text-xs font-bold hover:underline">View All Invoices</Link>
                        </div>
                        <div className="px-8 pb-8">
                            {stats?.recent_invoices?.length > 0 ? (
                                <div className="space-y-4">
                                    {stats.recent_invoices.slice(0, 5).map((invoice: any) => (
                                        <Link
                                            to={`/invoices/${invoice.id}`}
                                            key={invoice.id}
                                            className="block p-5 bg-slate-50 hover:bg-indigo-50/50 rounded-2xl border border-slate-100 transition-all group"
                                        >
                                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                                <div className="flex items-center gap-4">
                                                    <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center text-slate-400 group-hover:text-indigo-600 transition-colors shadow-sm">
                                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                        </svg>
                                                    </div>
                                                    <div>
                                                        <Text weight="black" color="primary" truncate className="uppercase">{invoice.invoice_number}</Text>
                                                        <Text variant="small" weight="bold" color="slate">{invoice.customer_name}</Text>
                                                    </div>
                                                </div>
                                                <div className="flex items-center justify-between sm:text-right gap-6">
                                                    <div className="sm:hidden">
                                                        <Text variant="label" color="slate">AMOUNT</Text>
                                                    </div>
                                                    <div>
                                                        <Text variant="h3" align="right" className="leading-none mb-1">₹{invoice.total_amount?.toLocaleString()}</Text>
                                                        <Text variant="small" align="right" color="slate">{new Date(invoice.invoice_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</Text>
                                                    </div>
                                                </div>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-12 bg-slate-50 rounded-3xl border border-dashed border-slate-200">
                                    <p className="text-slate-500 font-medium">No recent invoices found.</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Recent Orders */}
                    <div className="bg-white rounded-lg border border-slate-100 shadow-xl shadow-slate-200/50 overflow-hidden">
                        <div className="p-8 pb-4 flex items-center justify-between">
                            <Text variant="h3">Recent Orders</Text>
                            <Link to="/orders" className="text-indigo-600 text-xs font-bold hover:underline">Manage Orders</Link>
                        </div>
                        <div className="px-8 pb-8">
                            {stats?.recent_orders?.length > 0 ? (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {stats.recent_orders.slice(0, 4).map((order: any) => (
                                        <Link
                                            to={`/orders/${order.id}`}
                                            key={order.id}
                                            className="p-5 bg-white rounded-2xl border border-slate-100 hover:border-indigo-200 hover:shadow-lg transition-all"
                                        >
                                            <div className="flex justify-between items-start mb-4">
                                                <div className="px-3 py-1 bg-slate-100 rounded-lg">
                                                    <Text variant="label" color="secondary">{order.order_number}</Text>
                                                </div>
                                                <div className={`w-2 h-2 rounded-full ${order.status === 'pending' ? 'bg-amber-400 animate-pulse' : 'bg-emerald-400'}`}></div>
                                            </div>
                                            <Text weight="black" color="primary" truncate className="mb-1">{order.customer_name}</Text>
                                            <div className="flex items-baseline gap-2">
                                                <Text variant="h3" color="indigo">₹{order.total_amount?.toLocaleString()}</Text>
                                                <Text variant="label" color="slate">{order.status}</Text>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-12 bg-slate-50 rounded-3xl border border-dashed border-slate-200">
                                    <p className="text-slate-500 font-medium">No recent orders found.</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Dashboard;
